import { ControlledAutocompleteInput, ControlledInput, Indent, InputMasks, Stack } from '@ui-components/neo.tax';
import { ReactElement } from 'react';

import { WithPayrollExpense, PayrollQre } from '@neo.tax/rd/hooks/usePayrollQres';
import {
  COUNTRY_OPTIONS,
  PAYROLL_PROVIDER_NAMES,
  RdCategory,
  STATE_OPTIONS,
  useCurrentUserScopeForCurrentCompany,
  validateAmount,
} from '@neo.tax/shared';

import { usePayrollExpenseDetailsForm, usePayrollExpenseDetailsFormAnalyticIds } from './hooks';

type PayrollExpenseDetailsFormProps = {
  payrollQre: WithPayrollExpense<PayrollQre>;
};

export const PayrollExpenseDetailsForm = ({ payrollQre }: PayrollExpenseDetailsFormProps): ReactElement => {
  const { limitAccessToFinancialData } = useCurrentUserScopeForCurrentCompany();
  const { amountAnalyticId, countryAnalyticId, jobTitleAnalyticId, stateAnalyticId } =
    usePayrollExpenseDetailsFormAnalyticIds();
  const { control, watch } = usePayrollExpenseDetailsForm({ payrollQre });
  const countryCodeAlpha2 = watch('countryCodeAlpha2');

  const isEmployee = payrollQre.rdCategory === RdCategory.Employee;
  const isContractor = payrollQre.rdCategory === RdCategory.Contractor;
  const isCountryUnitedStates = countryCodeAlpha2 === 'US';
  const amountLabel = isEmployee ? 'Wages (W-2 Box 1)' : 'Amount';
  const payrollProvider = payrollQre.payrollProvider;
  const payrollProviderLabel =
    payrollProvider && PAYROLL_PROVIDER_NAMES[payrollProvider] ? PAYROLL_PROVIDER_NAMES[payrollProvider] : '';

  if (limitAccessToFinancialData) {
    return <></>;
  }
  return (
    <Stack as="form" direction="column" paddingX={4} fullHeight>
      <ControlledInput
        label={`${amountLabel} - ${payrollProviderLabel}`}
        name="amountDollars"
        rules={{
          required: `${amountLabel} is required.`,
          validate: validateAmount,
        }}
        id={amountAnalyticId}
        control={control}
        mask={InputMasks.NUMBER}
        maskReturnValueType="unmaskedValue"
        inlineLeadingContent="$"
        min={0}
      />
      <ControlledAutocompleteInput
        label="Country"
        name="countryCodeAlpha2"
        options={COUNTRY_OPTIONS}
        optionValueKey="codeAlpha2"
        optionDisplayKey="name"
        searchKeys={['name', 'codeAlpha2', 'codeAlpha3']}
        groupEndValues={['US']}
        rules={{
          required: {
            value: isEmployee || isContractor,
            message: 'Country is required.',
          },
        }}
        id={countryAnalyticId}
        control={control}
      />
      {isCountryUnitedStates && (
        <Indent>
          <ControlledAutocompleteInput
            label="State"
            name="stateCodeAlpha2"
            options={STATE_OPTIONS}
            searchKeys={['label', 'value']}
            rules={{
              required: {
                value: isEmployee || isContractor,
                message: 'State is required.',
              },
            }}
            id={stateAnalyticId}
            control={control}
          />
        </Indent>
      )}
      <ControlledInput
        label="Job Title"
        name="title"
        rules={{ required: 'Job Title is required for employees.' }}
        id={jobTitleAnalyticId}
        control={control}
      />
    </Stack>
  );
};
